import './_reorder.scss'
import { useTranslation } from 'react-i18next'

import EditImageModal from 'components/UI/Modal/EditImage/EditImageModal'
import { useModalContext } from 'context/modalContext'
import ProcessingError from 'features/ProcessingError/ProcessingError'
import ProcessingFile from 'features/ProcessingFile/ProcessingFile'
import UploadedFile from 'features/UploadedFile/UploadedFile'
import { type IMediaItem } from 'types/interfaces/ITypes'

interface PostImageProps {
  isEdit: boolean
  editImage?: IMediaItem
  image?: File
  imagePreview?: string
  id: number
  processing: boolean
  percentage: number
  uploadError: string | null
  previewUrl: string
  locked: boolean
  isPremium: boolean
  editPreview: () => void
  abortUploadCb: () => void
  removeFile: () => void
  removeByUrl?: (url: string) => void
  removePreviewPhoto?: (id: number) => void
  reorderActive?: boolean
  reorderNumber?: number
  updateOrder?: () => void
  handleToggleMediaLocked: (idx: number) => void
  setPreviewOpen?: (open: boolean) => void
  setPreviewMedia?: (postId: string) => void
  mediaDta?: any
  attachmentCount?: number

}

export const PostImage = ({
  isEdit,
  image,
  imagePreview,
  id,
  processing,
  percentage,
  uploadError,
  previewUrl,
  locked,
  isPremium,
  editPreview,
  abortUploadCb,
  removeFile,
  removeByUrl,
  reorderNumber,
  reorderActive,
  updateOrder,
  handleToggleMediaLocked,
  setPreviewOpen,
  setPreviewMedia,
  mediaDta,
  attachmentCount
}: PostImageProps) => {
  const modalData = useModalContext()
  const { t } = useTranslation()

  const toggleImageEdit = (img: File, id: number) => {
    modalData.addModal(
      t('editImage'),
      <EditImageModal imgId={id} imgSrc={imagePreview ?? ''} imgName={img.name} />
    )
  }

  // Derived readiness
  const imageReady = !processing && !!previewUrl && !uploadError

  if (uploadError) {
    return <ProcessingError uploadError={uploadError} onClose={removeFile} />
  }

  if (processing || percentage < 100) {
    const label = typeof percentage === 'number' && percentage < 100
      ? 'Uploading…'
      : 'Uploading…'

    return (
      <ProcessingFile
        percentage={Math.min(Number(percentage ?? 0), 100)}
        text={label}
        onAbort={() => {
          // Allow abort only while uploading
          if (typeof percentage === 'number' && percentage < 100) {
            abortUploadCb()
          }
          removeFile()
        }}
        className='processingFile__image'
      />
    )
  }

  if (imageReady) {
    return (
      <UploadedFile
        type='image'
        fileBlob={imagePreview ?? previewUrl}
        removeFile={isEdit ? () => removeByUrl?.(previewUrl) : removeFile}
        toggleEdit={() => {
          if (!reorderActive) {
            if (image) toggleImageEdit(image, id)
          } else {
            updateOrder?.()
          }
        }}
        customClass={`${reorderActive ? 'newpost__reorder__file' : ''}`}
        setPreviewOpen={setPreviewOpen}
        mediaDta={mediaDta}
        setPreviewMedia={setPreviewMedia}
        hasReorderCircle={reorderActive}
        reorderNumber={reorderNumber}
        editPreview={editPreview}
        cover={id === 0 && !locked}
        isPremium={isPremium}
        locked={locked}
        handleToggleMediaLocked={() => handleToggleMediaLocked(id)}
        attachmentCount={attachmentCount}
      />
    )
  }

  return null
}
